/**
 * The last onboarding screen: labs and answers have become a formula, and a physician has it
 * (ONB-022, ONB-023). The mix is shown as a finished, unsigned sheet. Nothing is charged until
 * they sign. The draft POST from the questions step is not awaited, so this screen polls until
 * the in-review version exists.
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useState } from "react";
import type { CurrentFormula, FormulaLine } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/motion.stylex";
import { motion } from "@lattice/tokens/motion.stylex";
import "@lattice/tokens/shadow.stylex";
import { shadow } from "@lattice/tokens/shadow.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { paper } from "@lattice/tokens/themes";
import "@lattice/tokens/breakpoints.stylex";
import { breakpoints } from "@lattice/tokens/breakpoints.stylex";
import { Button } from "@lattice/ui/Button";
import { ErrorState } from "@lattice/ui/ErrorState";
import { Pending } from "@lattice/ui/Pending";
import { Status } from "@lattice/ui/Status";
import { loadCurrentFormula } from "../api.ts";
import { navigate } from "../router.ts";
import { Shell, stepText } from "./Shell.tsx";
import { WhatHappens } from "./WhatHappens.tsx";
const NARROW = "@media (max-width: 480px)";

/** U+2011, so "add‑on" never breaks across a line. */
const NB = "‑";
const print = "xkrgsfx-B";
const _temp = {
  kKxzle: "xvo38ju",
  "$$css": true
};
const styles = {
  sheet: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1r0x45p",
    kmVPX3: "xq24xx7",
    kaIpWk: "x1hegsvj",
    kWkggS: "xcqm4px",
    kGVxlE: "x8xb0n0",
    $$css: true
  },
  row: {
    k1xSpc: "xrvj5dj",
    kumcoG: "x52fmzj",
    k1C7PZ: "x1vdzsm",
    khm7nJ: "xcfm6fe",
    k8WAf4: "xp1t01x",
    kL6WhQ: "x1t74ka8",
    kfdmCh: "x1q0q8m5",
    kt9PQ7: "xso031l",
    kKVMdj: "xi1bcxy x1ah4f44",
    k44tkh: "xcdydla",
    kyAemX: "x1ip1z55",
    kWV6AL: "x1u6ievf",
    $$css: true
  },
  delay: (beats: number) => [_temp, {
    "--x-animationDelay": (val => typeof val === "number" ? val + "ms" : val != null ? val : undefined)(`calc(${motion.stage} * ${beats})`)
  }]
};
function formatDose(dose: number, unit: string): string {
  return `${dose.toLocaleString("en-US")} ${unit}`;
}

/** How long the page waits for a formula to appear before it says something is wrong. */
const STALLED_MS = 120_000;
function useCurrentFormula() {
  const [formula, setFormula] = useState<CurrentFormula | null>(null);
  const [stalled, setStalled] = useState(false);
  useEffect(() => {
    let gone = false;
    const tick = async () => {
      const next = await loadCurrentFormula().catch(() => null);
      if (!gone && next) setFormula(next);
    };
    void tick();
    const id = setInterval(() => void tick(), 800);
    const late = setTimeout(() => setStalled(true), STALLED_MS);
    return () => {
      gone = true;
      clearInterval(id);
      clearTimeout(late);
    };
  }, []);
  return {
    formula,
    stalled: stalled && formula === null
  };
}
function qualifier(item: FormulaLine): string | null {
  if (item.isProteinBase) return "protein base";
  if (item.separateAddon) return `add${NB}on`;
  return null;
}
function Line({
  item,
  index
}: {
  readonly item: FormulaLine;
  readonly index: number;
}) {
  const extra = qualifier(item);
  return <li {...stylex.props(styles.row, styles.delay(index * 0.35))}>
      <span className="xqu410z x1e5flnj">
        {item.name}
        {extra !== null && <span className="x1jty25b xox936i">, {extra}</span>}
      </span>
      <span className="x1l1vl4p xqu410z xp4054r x1e5flnj">{formatDose(item.dose, item.unit)}</span>
      {item.sentence !== "" && <span className="x1osaytk xl4dl4e xgjxun8 x1fzhlzt">{item.sentence}</span>}
    </li>;
}
function FormulaSheet({
  formula
}: {
  readonly formula: CurrentFormula;
}) {
  return <section {...stylex.props(paper, styles.sheet)} data-testid="formula">
      <div className="x78zum5 x1q0g3np x1rpgqan x1qughib x1pha0wt x1nw9eq5 x626il7 x1ovbggj x1xx6wn7 x1q0q8m5 xlxy82">
        <h2 id="mix-heading" className="x1ghz6dp x11mdwsw x117lpx5 x1e5flnj">
          Your formula
        </h2>
        <span className="xl4dl4e x1jty25b x1fzhlzt">Waiting for a signature</span>
      </div>
      <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf" aria-labelledby="mix-heading">
        {formula.ingredients.map((item, index) => <Line key={item.ingredientId} item={item} index={index} />)}
      </ul>
      <div className="x78zum5 xdt5ytf x1173whf xap4y5k">
        <span className="x1brf3r0 xjm9jq1 x77asg4" aria-hidden="true" />
        <p className="x1ghz6dp xl4dl4e x1jty25b x1fzhlzt">Your physician signs here before anything is mixed.</p>
      </div>
    </section>;
}
function signedLead(email: string | null): string {
  return `They review your formula themselves, within 24 hours. We'll email ${email ?? "you"} when it's signed.`;
}
export function Reviewing() {
  const {
    formula,
    stalled
  } = useCurrentFormula();
  const email = sessionStorage.getItem("lattice-email");
  return <Shell title="Your formula is with a physician" step={3} of={3} aside={<WhatHappens />}>
      <div {...stylex.props(stepText.block)}>
        <h1 {...stylex.props(stepText.heading)}>Your formula is with a physician</h1>
        <p {...stylex.props(stepText.lead)}>{signedLead(email)}</p>
      </div>
      {formula ? <>
          <div className="x78zum5 xdt5ytf x626il7">
            <Status>Waiting for a signature</Status>
            <p className="x1ghz6dp x1jty25b x1fzhlzt">
              Nothing is charged until they approve. You can close this tab.
            </p>
          </div>
          <FormulaSheet formula={formula} />
        </> : stalled ? <ErrorState title="Your formula is taking longer than usual" body="Check back in a few minutes, or write to support if it does not appear." onRetry={() => window.location.reload()} /> : <Pending message="We're making your formula from your labs." bones={["row", "row", "row", "row"]} />}
      <div {...stylex.props(stepText.actions)}>
        <Button tone="quiet" wide onClick={() => navigate("/start/questions")}>
          Review your answers
        </Button>
      </div>
    </Shell>;
}